import React, { useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  setSingleWinExceedsAmount,
  setBalanceIncreasesAmount,
  setBalanceDecreasesAmount,
  setStopOnSingleWinExceeds,
  setStopOnBalanceIncreases,
  setStopOnBalanceDecreases,
} from "../../../../State/actions";
import {
  selectSingleWinExceedsAmount,
  selectBalanceIncreasesAmount,
  selectBalanceDecreasesAmount,
} from "../../../../State/selectors";

/**
 * Hidden HTML inputs for autoplay popup
 * These are positioned off-screen but accessible to mobile keyboards
 * They sync with Redux state to control the autoplay stop conditions
 */
const HiddenAutoplayInputs = () => {
  const dispatch = useDispatch();

  // Get values from Redux
  const singleWinExceedsValue = useSelector(selectSingleWinExceedsAmount);
  const balanceIncreasesValue = useSelector(selectBalanceIncreasesAmount);
  const balanceDecreasesValue = useSelector(selectBalanceDecreasesAmount);

  // Refs for the inputs
  const singleWinExceedsRef = useRef(null);
  const balanceIncreasesRef = useRef(null);
  const balanceDecreasesRef = useRef(null);

  // Track if input should maintain focus (prevent accidental blur)
  const shouldMaintainFocusRef = useRef(false);

  // Store refs in window object so PopiCheckboxWithInput can access them
  useEffect(() => {
    window.autoplayInputRefs = {
      singleWinExceeds: singleWinExceedsRef,
      balanceIncreases: balanceIncreasesRef,
      balanceDecreases: balanceDecreasesRef,
      shouldMaintainFocusRef: shouldMaintainFocusRef,
    };

    return () => {
      delete window.autoplayInputRefs;
    };
  }, []);

  // Handle focus/blur to prevent accidental keyboard dismissal
  const handleFocus = (inputId) => {
    shouldMaintainFocusRef.current = true;
    // Store focus time to prevent immediate blur
    window[`${inputId}FocusTime`] = Date.now();
  };

  const handleBlur = (inputId, e) => {
    const focusTime = window[`${inputId}FocusTime`] || 0;
    const timeSinceFocus = Date.now() - focusTime;

    // Very aggressive blur prevention for iOS - prevent blur for 1 second
    // iOS Safari often blurs immediately after focus, so we need to be extremely persistent
    if (timeSinceFocus < 1000 && shouldMaintainFocusRef.current) {
      // Multiple refocus attempts to combat iOS's aggressive blur behavior
      const ref = window.autoplayInputRefs?.[inputId];
      if (ref?.current) {
        // Immediate refocus
        ref.current.focus();

        // Try click() method as well (sometimes works better on iOS)
        if (ref.current.click) {
          ref.current.click();
        }

        // Multiple refocus attempts at different intervals
        requestAnimationFrame(() => {
          if (ref?.current && shouldMaintainFocusRef.current) {
            ref.current.focus();
          }
        });

        setTimeout(() => {
          if (ref?.current && shouldMaintainFocusRef.current) {
            ref.current.focus();
          }
        }, 5);

        setTimeout(() => {
          if (ref?.current && shouldMaintainFocusRef.current) {
            ref.current.focus();
          }
        }, 20);

        setTimeout(() => {
          if (ref?.current && shouldMaintainFocusRef.current) {
            ref.current.focus();
          }
        }, 50);

        setTimeout(() => {
          if (ref?.current && shouldMaintainFocusRef.current) {
            ref.current.focus();
          }
        }, 100);

        setTimeout(() => {
          if (ref?.current && shouldMaintainFocusRef.current) {
            ref.current.focus();
          }
        }, 200);
      }
      return;
    }

    shouldMaintainFocusRef.current = false;
    delete window[`${inputId}FocusTime`];
  };

  // Handle input changes
  const handleSingleWinExceedsChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    dispatch(setSingleWinExceedsAmount(value));
    if (value && value !== "") {
      dispatch(setStopOnSingleWinExceeds(true));
    } else if (value === "") {
      dispatch(setStopOnSingleWinExceeds(false));
    }
  };

  const handleBalanceIncreasesChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    dispatch(setBalanceIncreasesAmount(value));
    if (value && value !== "") {
      dispatch(setStopOnBalanceIncreases(true));
    } else if (value === "") {
      dispatch(setStopOnBalanceIncreases(false));
    }
  };

  const handleBalanceDecreasesChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "");
    dispatch(setBalanceDecreasesAmount(value));
    if (value && value !== "") {
      dispatch(setStopOnBalanceDecreases(true));
    } else if (value === "") {
      dispatch(setStopOnBalanceDecreases(false));
    }
  };

  // iOS Safari requires inputs to be in viewport and focusable
  // Position at top-left corner, tiny size, fully transparent
  const inputStyle = {
    position: "fixed",
    opacity: 0,
    top: "0px",
    left: "0px",
    width: "1px",
    height: "1px",
    zIndex: -1,
    // Don't use pointerEvents: "none" - iOS needs it to be focusable
    // Instead, make it tiny and transparent so it's invisible but functional
    border: "none",
    outline: "none",
    background: "transparent",
    color: "transparent",
  };

  return (
    <>
      <input
        ref={singleWinExceedsRef}
        id="singleWinExceeds"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={singleWinExceedsValue}
        onChange={handleSingleWinExceedsChange}
        onFocus={() => handleFocus("singleWinExceeds")}
        onBlur={(e) => handleBlur("singleWinExceeds", e)}
        onTouchStart={(e) => {
          // Ensure focus on touch for iOS
          if (singleWinExceedsRef.current) {
            singleWinExceedsRef.current.focus();
          }
        }}
        style={inputStyle}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        readOnly={false}
        tabIndex={-1}
      />
      <input
        ref={balanceIncreasesRef}
        id="balanceIncreases"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={balanceIncreasesValue}
        onChange={handleBalanceIncreasesChange}
        onFocus={() => handleFocus("balanceIncreases")}
        onBlur={(e) => handleBlur("balanceIncreases", e)}
        onTouchStart={(e) => {
          // Ensure focus on touch for iOS
          if (balanceIncreasesRef.current) {
            balanceIncreasesRef.current.focus();
          }
        }}
        style={inputStyle}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        readOnly={false}
        tabIndex={-1}
      />
      <input
        ref={balanceDecreasesRef}
        id="balanceDecreases"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={balanceDecreasesValue}
        onChange={handleBalanceDecreasesChange}
        onFocus={() => handleFocus("balanceDecreases")}
        onBlur={(e) => handleBlur("balanceDecreases", e)}
        onTouchStart={(e) => {
          // Ensure focus on touch for iOS
          if (balanceDecreasesRef.current) {
            balanceDecreasesRef.current.focus();
          }
        }}
        style={inputStyle}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
        readOnly={false}
        tabIndex={-1}
      />
    </>
  );
};

export default HiddenAutoplayInputs;
